class MockSocket {
    constructor() { this._listeners = {} }
    on(event, cb) {
        if (!this._listeners[event]) this._listeners[event] = []
        this._listeners[event].push(cb)
    }
    off(event, cb) {
        if (!this._listeners[event]) return
        if (cb) this._listeners[event] = this._listeners[event].filter(l => l !== cb)
        else delete this._listeners[event]
    }
    emit(event, data) {
        if (!this._listeners[event]) return
        this._listeners[event].forEach(cb => cb(data))
    }
    disconnect() {}
    connect() {}
}

function toBoolean(value, fallback) {
    if (value === undefined || value === '') return fallback
    return ['1', 'true', 'yes', 'on'].includes(String(value).toLowerCase())
}

function toNumber(value) {
    if (!value) return undefined
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : undefined
}

function normalizeWebsocketEndpoint(host, path) {
    const endpoint = { host: host.trim(), path: path.trim() }
    if (!endpoint.host) return endpoint

    const hostWithProtocol = /^[a-z][a-z\d+\-.]*:\/\//i.test(endpoint.host)
        ? endpoint.host
        : `https://${endpoint.host}`

    try {
        const parsed = new URL(hostWithProtocol)
        endpoint.host = parsed.hostname
        if (!endpoint.path && parsed.pathname !== '/') endpoint.path = parsed.pathname
    } catch {
        const [nextHost, ...pathParts] = endpoint.host.split('/')
        endpoint.host = nextHost
        if (!endpoint.path && pathParts.length > 0) endpoint.path = `/${pathParts.join('/')}`
    }

    if (endpoint.path && !endpoint.path.startsWith('/')) endpoint.path = `/${endpoint.path}`
    return endpoint
}

export default ({ app, store }, inject) => {
    const useMock = app.$config.mockSocket === true

    if (useMock) {
        const socket = new MockSocket()
        window.__socket = socket
        console.info('[socket] MOCK mode active — use window.__socket.emit(...) to test')
        inject('notifications', socket)
        return
    }

    const Pusher = require('pusher-js')

    const config = {
        appKey: app.$config.PUSHER_APP_KEY,
        cluster: app.$config.PUSHER_CLUSTER || 'mt1',
        host: app.$config.PUSHER_HOST || '',
        wsPath: app.$config.PUSHER_WS_PATH || '',
        wsPort: toNumber(app.$config.PUSHER_WS_PORT),
        wssPort: toNumber(app.$config.PUSHER_WSS_PORT),
        forceTLS: toBoolean(app.$config.PUSHER_FORCE_TLS, false),
        authEndpoint: app.$config.AUTH_API + '/api/broadcasting/auth',
    }

    const wsEndpoint = normalizeWebsocketEndpoint(config.host, config.wsPath)

    const options = {
        cluster: config.cluster,
        forceTLS: config.forceTLS,
        channelAuthorization: {
            endpoint: config.authEndpoint,
            transport: 'ajax',
            headersProvider: () => ({
                // reads token from Vuex at call time, not at init time
                Authorization: `Bearer ${localStorage.getItem('jwt') || ''}`,
                Accept: 'application/json',
            }),
        },
    }

    if (wsEndpoint.host) {
        options.wsHost = wsEndpoint.host
        options.enabledTransports = ['ws']
    }
    if (wsEndpoint.path) options.wsPath = wsEndpoint.path
    if (config.wsPort) options.wsPort = config.wsPort
    if (config.wssPort) options.wssPort = config.wssPort

    const pusher = new Pusher(config.appKey, options)

    pusher.connection.bind('state_change', ({ current }) => {
        console.info('[socket] connection state:', current)
    })

    pusher.connection.bind('error', (err) => {
        console.error('[socket] connection error:', err)
    })

    // wrap pusher into a socket-like interface
    // so this.$socket.on/off works the same as mock mode
    const channels = {}

    const socket = {
        _channels: channels,
        subscribe(channelName) {
            if (!channels[channelName]) {
                channels[channelName] = pusher.subscribe(channelName)
            }
            return channels[channelName]
        },
        on(channelName, event, cb) {
            const ch = this.subscribe(channelName)
            ch.bind(event, cb)
        },
        off(channelName, event, cb) {
            if (!channels[channelName]) return
            channels[channelName].unbind(event, cb)
        },
        unsubscribe(channelName) {
            pusher.unsubscribe(channelName)
            delete channels[channelName]
        },
        subscribePrivate(userId) {
            return this.subscribe(`private-user.${userId}`)
        },
        disconnect() { pusher.disconnect() },
        connect() { pusher.connect() },
        pusher, // expose raw pusher if ever needed
    }

    window.__socket = socket // available in devtools always
    inject('notifications', socket)
}